import { HttpErrorResponse } from '@angular/common/http';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  HostListener,
  inject,
  input,
  signal,
  ViewChild,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { GenerateAiImageBodyFluxProDto, GenerateAiImageBodyFluxSchnellDto } from '@nusign/picflow-backend-types';
import { catchError, filter, startWith, take, throwError } from 'rxjs';
import { BadgeComponent } from '../../../components/badge/badge.component';
import { debounce } from '../../../lib/debounce';
import { isMetaKey } from '../../../lib/is-meta-key';
import { AiService } from '../../../services/ai.service';
import { CreditService } from '../../../services/credit.service';
import { ToastService } from '../../../services/toast.service';
import { LoadingIndicatorComponent } from '../../../shared/components/loading-indicator/loading-indicator.component';
import { SvgComponent } from '../../../shared/components/svg/svg.component';
import { SwitcherComponent } from '../../../shared/components/switcher/switcher.component';
import { AutofocusDirective } from '../../../shared/directives/autofocus.directive';
import { ButtonDirective } from '../../../shared/directives/button.directive';
import { SwitcherItemDirective } from '../../../shared/directives/switcher-item.directive';
import { TooltipDirective } from '../../../shared/directives/tooltip.directive';
import { WysiwygDirective } from '../../../wysiwyg/directives/wysiwyg.directive';

@Component({
  selector: 'app-image-generator-prompt',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    SwitcherComponent,
    SwitcherItemDirective,
    BadgeComponent,
    ButtonDirective,
    AutofocusDirective,
    LoadingIndicatorComponent,
    SvgComponent,
    WysiwygDirective,
    TooltipDirective,
  ],
  templateUrl: './image-generator-prompt.component.html',
  styleUrl: './image-generator-prompt.component.scss',
})
export class ImageGeneratorPromptComponent {
  private aiService = inject(AiService);
  private toasts = inject(ToastService);
  private creditService = inject(CreditService);
  @ViewChild('promptInputEl') promptInputEl: ElementRef<HTMLDivElement>;
  overview$ = this.creditService.overview();
  overview = toSignal(this.overview$);

  @HostListener('window:keydown', ['$event'])
  keyDownEvent(event: KeyboardEvent) {
    if (this.promptInputEl.nativeElement !== document.activeElement) return;
    const metaKey = isMetaKey(event);
    if (event.key === 'Enter' && metaKey) {
      event.preventDefault();
      event.stopPropagation();
      this.submit();
    }
  }

  public form = new FormGroup({
    prompt: new FormControl('', [Validators.required]),
    model: new FormControl<'fast' | 'ultra'>('fast'),
    aspectRatio: new FormControl<'1:1' | '3:2' | '2:3'>('1:1'),
  });
  promptInput = '';
  generating = signal(false);
  sectionId = input.required<string>();

  model = toSignal(this.form.controls.model.valueChanges.pipe(startWith(this.form.controls.model.value)));

  creditCost = computed(() => {
    const overview = this.overview();
    const model = this.model();
    if (!overview || !model) return;
    const params = this.calcParams();
    return overview.cost.ai.image_generation[params.model] * (params['num_outputs'] || 1);
  });

  updatePrompt = debounce(() => {
    const prompt = this.promptInput
      .trim()
      .replace(/<br>/g, '\n')
      .replace(/<[^>]*>?/gm, '')
      .replace(/&nbsp;/g, ' ')
      .replace(/&#160;/g, ' ')
      .trim();
    this.form.controls.prompt.setValue(prompt);
  }, 0);

  submit() {
    if (!this.form.valid) return;
    if (this.generating()) return;
    this.generating.set(true);
    const params = this.calcParams();
    this.aiService
      .generate(params, this.sectionId())
      .pipe(
        filter(progress => progress?.type === 'success' || progress?.type === 'error'),
        take(1),
        catchError(error => {
          if (!(error instanceof HttpErrorResponse)) return throwError(() => error);
          this.generating.set(false);
          console.log(error);
          console.log(error.error);

          this.toasts.add(error.error.message, 'error');
        })
      )
      .subscribe(() => {
        this.generating.set(false);
      });
  }

  selectModel(model: 'fast' | 'ultra') {
    this.form.controls.model.setValue(model);
  }

  private calcParams() {
    if (this.form.value.model === 'fast') {
      return <GenerateAiImageBodyFluxSchnellDto>{
        model: 'black-forest-labs/flux-schnell',
        prompt: this.form.value.prompt,
        aspect_ratio: this.form.value.aspectRatio,
        num_outputs: 4,
        output_format: 'jpg',
      };
    }
    return <GenerateAiImageBodyFluxProDto>{
      model: 'black-forest-labs/flux-1.1-pro',
      prompt: this.form.value.prompt,
      aspect_ratio: this.form.value.aspectRatio,
      output_format: 'jpg',
    };
  }

  close() {
    this.aiService.showAiUi$.next(undefined);
  }
}
